import { useMemo } from 'react';
import { useRouter } from 'next/router';

import useBreadcrumbsAndGeonameId from 'hooks/useBreadcrumbsAndGeonameId';
import useMapLocation from 'hooks/map/useMapLocation';
import type { MapLayer } from 'types/map';

import {
  getBuoyMapPath,
  getBuoyMapPathWithLocation,
  getSpotMapPath,
  getSpotMapPathWithLocation,
  getWindstationMapPath,
  getWindStationMapPathWithLocation,
  getBuoyGeonameMapPath,
  getGeonameMapPath,
  getWindstationGeonameMapPath,
} from 'utils/urls/mapPaths';
import subregionForecastChartsPath, { forecastChartsPath } from 'utils/urls/forecastChartsPath';
import mapLayers from 'utils/mapLayers';

interface Props {
  activeMap?: MapLayer;
  center?: { lat: number; lon: number };
  isGeonameMap?: boolean;
  subregion?: any;
  subregions?: any;
  subregionId?: string;
}

const useMapControls = ({
  activeMap = mapLayers.SURF_SPOTS,
  center = { lat: 0, lon: 0 },
  isGeonameMap = false,
  subregion,
  subregions,
  subregionId,
}: Props) => {
  const router = useRouter();
  const { geonameId, breadcrumbs } = useBreadcrumbsAndGeonameId();
  const breadcrumbsUrl = breadcrumbs.join('/');
  const { location: mapV2Location } = useMapLocation();
  const { location } = router.query as { location: string };

  const spotLink = useMemo(() => {
    if (isGeonameMap) {
      return getGeonameMapPath({ breadcrumbs: breadcrumbsUrl, geonameId });
    }

    if (location) {
      return getSpotMapPathWithLocation(location);
    }
    return getSpotMapPath();
  }, [breadcrumbsUrl, geonameId, isGeonameMap, location]);

  const windStationLink = useMemo(() => {
    if (isGeonameMap) {
      return getWindstationGeonameMapPath({ breadcrumbs: breadcrumbsUrl, geonameId });
    }

    if (location) {
      return getWindStationMapPathWithLocation(location);
    }
    return getWindstationMapPath();
  }, [breadcrumbsUrl, geonameId, isGeonameMap, location]);

  const buoyLink = useMemo(() => {
    if (isGeonameMap) {
      return getBuoyGeonameMapPath({ breadcrumbs: breadcrumbsUrl, geonameId });
    }

    if (location) {
      return getBuoyMapPathWithLocation(location);
    }
    return getBuoyMapPath();
  }, [breadcrumbsUrl, geonameId, isGeonameMap, location]);

  const chartLink = useMemo(() => {
    if (activeMap === mapLayers.SURF_SPOTS) {
      return subregionForecastChartsPath(
        subregionId || subregions?.subregions?.[0]?._id || '',
        subregion?.name || subregions?.subregions?.[0]?.subregion?.name || '',
        'wave-height',
        { center, zoom: 7 },
      );
    }

    /*
    The surf spots and geoname surf spots map pages presently use our map reducer location over the mapV2 reducer location.
    This means that for constructing a charts URL in the buoys, windstations, and geonames pages for both of those two, we want to
    rely on the mapv2 reducer location.
    */
    return forecastChartsPath('wave-height', mapV2Location);
  }, [activeMap, center, mapV2Location, subregion, subregionId, subregions]);

  return {
    spotLink,
    buoyLink,
    chartLink,
    windStationLink,
  };
};

export default useMapControls;
